<template>
  <div class="subcompany-form-wrapper">
    <!-- Toast Notifications -->
    <div class="toast-container">
      <CToaster :autohide="3000">
        <CToast
          v-for="info in infoList"
          :key="info.id"
          :show="true"
          :header="info.header"
          :color="info.color"
        >
          {{ info.message }}
        </CToast>
      </CToaster>
    </div>

    <!-- Page Header -->
    <CRow class="mb-4">
      <CCol>
        <div class="page-header">
          <h4 class="mb-0">
            <i class="fas fa-building me-2 text-primary"></i>
            <strong>{{ obj.id ? 'Edit' : 'Add' }} Sub-Company</strong>
          </h4>
          <small class="text-muted">{{ obj.id ? 'Update existing sub-company information' : 'Create a new sub-company record' }}</small>
        </div>
      </CCol>
    </CRow>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="text-center py-5">
        <CSpinner color="primary" />
        <p class="text-muted mt-3">Loading sub-company data...</p>
      </div>
    </div>

    <!-- SubCompany Form -->
    <CRow v-else>
      <CCol sm="12">
        <CCard class="form-card">
          <CCardHeader class="form-header">
            <div class="d-flex align-items-center">
              <i class="fas fa-sitemap me-2 text-primary"></i>
              <strong>Sub-Company Information</strong>
            </div>
          </CCardHeader>
          <CCardBody>
            <CForm>
              <!-- Name -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Name</strong>
                  <span class="text-danger ms-1">*</span>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="$v.obj.name.$model"
                    :isValid="checkIfValid('name')"
                    :readonly="isReadOnly"
                    invalidFeedback="Sub-company name is required (minimum 2 characters)"
                    placeholder="Enter sub-company name"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-info-circle me-1"></i>
                    Official name of the sub-company
                  </small>
                </CCol>
              </CRow>

              <!-- Registration Number -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Registration No</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.regNo"
                    :readonly="isReadOnly"
                    placeholder="Enter registration number"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-id-card me-1"></i>
                    Company registration number
                  </small>
                </CCol>
              </CRow>

              <!-- Tagline -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Tagline</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.tagLine"
                    :readonly="isReadOnly"
                    placeholder="Enter company tagline"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-quote-left me-1"></i>
                    Company slogan or tagline
                  </small>
                </CCol>
              </CRow>

              <!-- Address -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Address</strong>
                </CCol>
                <CCol sm="9">
                  <CTextarea
                    v-model="obj.address"
                    :readonly="isReadOnly"
                    placeholder="Enter address"
                    rows="3"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-map-marker-alt me-1"></i>
                    Full address of the sub-company
                  </small>
                </CCol>
              </CRow>

              <!-- Phone -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Phone</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.phone"
                    :readonly="isReadOnly"
                    placeholder="Enter phone number"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-phone me-1"></i>
                    Main contact number
                  </small>
                </CCol>
              </CRow>

              <!-- Company Logo -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Company Logo</strong>
                </CCol>
                <CCol sm="9">
                  <CImg
                    v-if="obj.logoUrl"
                    :src="getImageUrl()"
                    class="mb-2"
                    thumbnail
                    style="max-width: 300px;"
                  />
                  <WidgetsUploadImage
                    :logoUrl="logoUrl"
                    @uploaded="uploaded"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-image me-1"></i>
                    Upload company logo image
                  </small>
                </CCol>
              </CRow>

              <!-- Bank Accounts Section (Edit Mode Only) -->
              <div v-if="obj.id">
                <hr class="my-4" />

                <!-- Bank Accounts Header -->
                <CRow class="mb-3">
                  <CCol>
                    <h6 class="section-header">
                      <i class="fas fa-university me-2 text-primary"></i>
                      <strong>Bank Accounts</strong>
                      <CBadge color="info" class="ms-2">{{ obj.bankAccounts ? obj.bankAccounts.length : 0 }} Accounts</CBadge>
                    </h6>
                  </CCol>
                </CRow>

                <!-- Bank Accounts Table -->
                <CRow class="mb-3" v-if="obj.bankAccounts && obj.bankAccounts.length > 0">
                  <CCol sm="12">
                    <div class="bank-accounts-table-wrapper">
                      <CDataTable
                        :items="obj.bankAccounts"
                        :fields="bankAccountFields"
                        hover
                        border
                        striped
                      >
                        <template #name="{ item }">
                          <td>
                            <i class="fas fa-university me-2 text-muted"></i>
                            <strong>{{ item.name }}</strong>
                          </td>
                        </template>
                        <template #show_details="{ item }">
                          <td class="py-2">
                            <CButton
                              size="sm"
                              color="info"
                              variant="outline"
                              class="me-1"
                              @click="onEditBankAccount(item)"
                            >
                              <i class="fas fa-edit me-1"></i>
                              Edit
                            </CButton>
                            <CButton
                              size="sm"
                              color="danger"
                              variant="outline"
                              @click="showDeleteBankAccountConfirmation(item)"
                            >
                              <i class="fas fa-trash-alt me-1"></i>
                              Delete
                            </CButton>
                          </td>
                        </template>
                      </CDataTable>
                    </div>
                  </CCol>
                </CRow>

                <!-- Empty Bank Accounts State -->
                <CRow class="mb-3" v-else>
                  <CCol sm="12">
                    <div class="empty-bank-accounts">
                      <i class="fas fa-university fa-3x text-muted mb-3"></i>
                      <p class="text-muted">No bank accounts added yet</p>
                      <small class="text-muted">Add your first bank account using the button below</small>
                    </div>
                  </CCol>
                </CRow>

                <!-- Add Bank Account Button -->
                <CRow class="mb-3">
                  <CCol sm="12">
                    <CButton
                      color="success"
                      @click="addBankAccountPopup()"
                    >
                      <i class="fas fa-plus-circle me-2"></i>
                      Add Bank Account
                    </CButton>
                  </CCol>
                </CRow>
              </div>

              <!-- Info Message for New Sub-Company -->
              <CRow v-else class="mb-3">
                <CCol sm="12">
                  <CAlert color="info" :show="true">
                    <i class="fas fa-info-circle me-2"></i>
                    <strong>Note:</strong> You can add bank accounts after creating the sub-company.
                  </CAlert>
                </CCol>
              </CRow>
            </CForm>
          </CCardBody>

          <!-- Form Footer -->
          <CCardFooter class="form-footer">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <CButton
                  v-if="canSubmit"
                  color="primary"
                  :disabled="!isValid || submitting"
                  @click="onSubmit"
                >
                  <CSpinner v-if="submitting" size="sm" class="me-2" />
                  <i v-else class="fas fa-check-circle me-2"></i>
                  {{ submitting ? 'Saving...' : 'Submit' }}
                </CButton>
                <CButton
                  color="secondary"
                  class="ms-2"
                  @click="cancel"
                  :disabled="submitting"
                >
                  <i class="fas fa-times me-2"></i>
                  Cancel
                </CButton>
              </div>
              <div v-if="!isValid" class="text-danger">
                <i class="fas fa-exclamation-triangle me-2"></i>
                <small>Please fill in all required fields</small>
              </div>
            </div>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>

    <!-- Add/Edit Bank Account Modal -->
    <CModal
      :show.sync="addNewBankAccountPopupState"
      size="lg"
    >
      <template #header>
        <h5>
          <i class="fas fa-university me-2"></i>
          {{ newBankAccount.id ? 'Edit' : 'Add' }} Bank Account
        </h5>
      </template>
      <CForm>
        <CRow class="mb-3">
          <CCol tag="label" sm="3" class="col-form-label">
            <strong>Bank Name</strong>
          </CCol>
          <CCol sm="9">
            <CInput
              v-model="newBankAccount.name"
              placeholder="Enter bank name"
            />
          </CCol>
        </CRow>

        <CRow class="mb-3">
          <CCol tag="label" sm="3" class="col-form-label">
            <strong>Address</strong>
          </CCol>
          <CCol sm="9">
            <CTextarea
              v-model="newBankAccount.address"
              placeholder="Enter bank address"
              rows="3"
            />
          </CCol>
        </CRow>

        <CRow class="mb-3">
          <CCol tag="label" sm="3" class="col-form-label">
            <strong>Account Name</strong>
          </CCol>
          <CCol sm="9">
            <CInput
              v-model="newBankAccount.accountName"
              placeholder="Enter account name"
            />
          </CCol>
        </CRow>

        <CRow class="mb-3">
          <CCol tag="label" sm="3" class="col-form-label">
            <strong>Account No</strong>
          </CCol>
          <CCol sm="9">
            <CInput
              v-model="newBankAccount.accountNo"
              placeholder="Enter account number"
            />
          </CCol>
        </CRow>

        <CRow class="mb-3">
          <CCol tag="label" sm="3" class="col-form-label">
            <strong>Account Type</strong>
          </CCol>
          <CCol sm="9">
            <CInput
              v-model="newBankAccount.accountType"
              placeholder="Enter account type (e.g., Savings, Current)"
            />
          </CCol>
        </CRow>
      </CForm>
      <template #footer>
        <CButton color="secondary" @click="addNewBankAccountPopupState = false">
          Cancel
        </CButton>
        <CButton color="primary" @click="handleSaveBankAccount">
          <i class="fas fa-check me-2"></i>
          Save
        </CButton>
      </template>
    </CModal>

    <!-- Delete Bank Account Confirmation Modal -->
    <CModal
      :show.sync="warningDeleteBankAccountModal"
      color="danger"
    >
      <template #header>
        <h5>
          <i class="fas fa-exclamation-triangle me-2"></i>
          Confirm Delete
        </h5>
      </template>
      Are you sure you want to delete the bank account <strong>{{ itemBankAccountToDelete.name }}</strong>?
      <template #footer>
        <CButton color="secondary" @click="warningDeleteBankAccountModal = false">
          Cancel
        </CButton>
        <CButton color="danger" @click="handleDeleteBankAccount">
          <i class="fas fa-trash-alt me-2"></i>
          Delete
        </CButton>
      </template>
    </CModal>

    <!-- Force Delete Bank Account Modal (shown when account is linked to AP Companies) -->
    <CModal
      :show.sync="forceDeleteBankAccountModal"
      color="warning"
    >
      <template #header>
        <h5>
          <i class="fas fa-exclamation-triangle me-2"></i>
          Bank Account In Use
        </h5>
      </template>
      {{ forceDeleteBankAccountMessage }}
      <template #footer>
        <CButton color="secondary" @click="forceDeleteBankAccountModal = false">
          Cancel
        </CButton>
        <CButton color="warning" @click="handleForceDeleteBankAccount">
          <i class="fas fa-trash-alt me-2"></i>
          Proceed
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import SubCompanyApi from "../../lib/subCompanyApi";
import Helper from "../../lib/helper";
import { required, minLength } from "vuelidate/lib/validators";
import WidgetsUploadImage from "../widgets/WidgetsUploadImage";
import { v4 as uuidv4 } from "uuid";

const helper = new Helper();

export default {
  name: "SubCompany",
  components: {
    WidgetsUploadImage,
  },
  data: () => {
    return {
      warningDeleteBankAccountModal: false,
      itemBankAccountToDelete: {},
      forceDeleteBankAccountModal: false,
      forceDeleteBankAccountMessage: "",
      addNewBankAccountPopupState: false,
      newBankAccount: {
        id: null,
        name: "",
        address: "",
        accountName: "",
        accountNo: "",
        accountType: "",
      },
      bankAccountFields: [
        { key: "name", label: "Bank Name", _style: "min-width: 150px;" },
        { key: "address", label: "Address", _style: "min-width: 200px;" },
        { key: "accountName", label: "Account Name", _style: "min-width: 150px;" },
        { key: "accountNo", label: "Account No", _style: "min-width: 120px;" },
        { key: "accountType", label: "Account Type", _style: "min-width: 100px;" },
        {
          key: "show_details",
          label: "Actions",
          _style: "min-width:150px; width:2%",
          sorter: false,
          filter: false,
        },
      ],
      infoList: [],
      api: new SubCompanyApi(),
      obj: {
        name: "",
      },
      submitted: false,
      submitting: false,
      loading: false,
    };
  },
  mounted() {
    // Check view permission first
    if (!this.canView) {
      this.$router.push({ name: 'Page403' });
      return;
    }

    // If creating new, check CanAdd
    if (!this.$route.params.id && !this.canAdd) {
      this.$router.push('/admins/SubCompanyList');
      this.toast('Access Denied', 'You do not have permission to add sub companies', 'danger');
      return;
    }

    this.obj = this.getEmptyObj();
    this.resetObj();
  },
  validations: {
    obj: {
      name: {
        required,
        minLength: minLength(2),
      },
    },
  },

  computed: {
    formString() {
      return JSON.stringify(this.obj, null, 4);
    },
    isValid() {
      return !this.$v.obj.$invalid;
    },
    isDirty() {
      return this.$v.obj.$anyDirty;
    },
    canView() {
      return this.$auth.hasSubModulePermission('SUB_COMPANIES', 'CanView');
    },
    canAdd() {
      return this.$auth.hasSubModulePermission('SUB_COMPANIES', 'CanAdd');
    },
    canUpdate() {
      return this.$auth.hasSubModulePermission('SUB_COMPANIES', 'CanUpdate');
    },
    isReadOnly() {
      if (this.obj.id) {
        return !this.canUpdate;
      } else {
        return !this.canAdd;
      }
    },
    canSubmit() {
      if (this.obj.id) {
        return this.canUpdate;
      } else {
        return this.canAdd;
      }
    },
  },
  methods: {
    onEditBankAccount(item) {
      this.newBankAccount = { ...item };
      this.addNewBankAccountPopupState = true;
    },

    showDeleteBankAccountConfirmation(item) {
      this.itemBankAccountToDelete = item;
      this.warningDeleteBankAccountModal = true;
    },

    resetNewBankAccount() {
      this.newBankAccount.id = null;
      this.newBankAccount.name = "";
      this.newBankAccount.address = "";
      this.newBankAccount.accountName = "";
      this.newBankAccount.accountNo = "";
      this.newBankAccount.accountType = "";
      this.newBankAccount.subCompanyId = null;
    },

    addBankAccountPopup() {
      this.resetNewBankAccount();
      this.addNewBankAccountPopupState = true;
    },

    handleSaveBankAccount() {
      if (this.newBankAccount.id) {
        // Update existing bank account
        this.api
          .updateBankAccount(this.obj.id, this.newBankAccount)
          .then((response) => {
            this.toast("Success", "Bank account updated successfully", "success");
            this.addNewBankAccountPopupState = false;
            this.resetObj();
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
          });
      } else {
        // Add new bank account
        this.newBankAccount.id = uuidv4();
        this.newBankAccount.subCompanyId = this.obj.id;

        this.api
          .addBankAccount(this.obj.id, this.newBankAccount)
          .then((response) => {
            this.toast("Success", "Bank account added successfully", "success");
            this.addNewBankAccountPopupState = false;
            this.resetObj();
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
    },

    handleDeleteBankAccount() {
      this.api
        .removeBankAccount(this.itemBankAccountToDelete.id)
        .then((response) => {
          this.toast("Success", "Bank account deleted successfully", "success");
          this.warningDeleteBankAccountModal = false;
          this.itemBankAccountToDelete = {};
          this.resetObj();
        })
        .catch(({ data, status }) => {
          // 409 means the account is linked to AP Companies — show a force-delete confirmation
          if (status === 409) {
            this.warningDeleteBankAccountModal = false;
            this.forceDeleteBankAccountMessage =
              data && data.responseException && data.responseException.exceptionMessage
                ? data.responseException.exceptionMessage
                : helper.getErrorMessage(data);
            this.forceDeleteBankAccountModal = true;
          } else {
            this.toast("Error", helper.getErrorMessage(data), "danger");
          }
        });
    },

    handleForceDeleteBankAccount() {
      this.api
        .removeBankAccount(this.itemBankAccountToDelete.id, true)
        .then((response) => {
          this.toast("Success", "Bank account deleted successfully", "success");
          this.forceDeleteBankAccountModal = false;
          this.itemBankAccountToDelete = {};
          this.resetObj();
        })
        .catch(({ data }) => {
          this.toast("Error", helper.getErrorMessage(data), "danger");
          this.forceDeleteBankAccountModal = false;
        });
    },

    removeTrailingSlash(str) {
      if (str.endsWith("/")) {
        return str.slice(0, -1);
      }
      return str;
    },

    getImageUrl() {
      if (this.obj && this.obj.logoUrl) {
        var url = this.removeTrailingSlash(apiUrl) + this.obj.logoUrl;
        return url;
      }
      return "";
    },

    uploaded(data) {
      this.uploadedFiles = data.uploadedFiles;
      this.obj.logoUrl = this.uploadedFiles[0].url;
    },

    checkIfValid(fieldName) {
      const field = this.$v.obj[fieldName];
      return !(field.$invalid || field.$model === "");
    },

    onSubmit() {
      this.submitting = true;
      if (!this.obj.id) {
        this.api
          .create(this.obj)
          .then((response) => {
            this.toast("Success", "Sub-company created successfully", "success");
            setTimeout(() => {
              this.$router.push({ path: `/admins/SubCompany/${response.result.id}` });
            }, 1000);
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
            this.submitting = false;
          });
      } else {
        this.api
          .update(this.obj)
          .then((response) => {
            this.toast("Success", "Sub-company updated successfully", "success");
            setTimeout(() => {
              this.$router.push({ path: "/admins/SubCompanyList" });
            }, 1000);
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
            this.submitting = false;
          });
      }
    },

    toast(header, message, color) {
      const toastId = `toast-${Date.now()}-${Math.random()}`;
      this.infoList.push({
        id: toastId,
        header: header,
        message: message,
        color: color,
      });

      // Auto-remove toast after autohide duration (3000ms + small buffer)
      setTimeout(() => {
        const index = this.infoList.findIndex(info => info.id === toastId);
        if (index !== -1) {
          this.infoList.splice(index, 1);
        }
      }, 3500);
    },

    resetObj() {
      this.loading = true;
      if (this.$route.params.id) {
        this.api
          .get(this.$route.params.id)
          .then((response) => {
            this.obj = response.result;
            this.loading = false;
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
            this.loading = false;
          });
      } else {
        this.obj = this.getEmptyObj();
        this.loading = false;
      }
    },

    getEmptyObj() {
      return {
        name: "",
        regNo: "",
        tagLine: "",
        address: "",
        phone: "",
        logoUrl: "",
        bankAccounts: [],
      };
    },

    cancel() {
      this.$router.push({ path: "/admins/SubCompanyList" });
    },
  },
};
</script>

<style scoped>
/* Professional Container */
.subcompany-form-wrapper {
  padding: 1.5rem;
  min-height: 100vh;
}

/* Toast Notifications */
.toast-container {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1050;
}

/* Page Header */
.page-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
}

.page-header h4 {
  font-size: 1.5rem;
  color: var(--cui-body-color, #212529);
}

.page-header small {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875rem;
}

/* Loading State */
.loading-state {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Form Card */
.form-card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.form-header {
  background: white;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.25rem;
}

.form-header strong {
  font-size: 1.1rem;
  color: var(--cui-body-color, #212529);
}

/* Form Body */
.form-card .card-body {
  padding: 2rem 1.5rem;
}

/* Form Labels */
.col-form-label {
  font-weight: 500;
  color: var(--cui-body-color, #495057);
}

.col-form-label .text-danger {
  font-size: 1.2em;
}

/* Form Help Text */
.form-text {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

/* Section Header */
.section-header {
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--cui-border-color, #e9ecef);
}

/* Bank Accounts Table */
.bank-accounts-table-wrapper {
  border: 1px solid var(--cui-border-color, #dee2e6);
  border-radius: 0.25rem;
  overflow: hidden;
}

/* Empty Bank Accounts State */
.empty-bank-accounts {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: var(--cui-body-bg, #f8f9fa);
  border: 2px dashed var(--cui-border-color, #dee2e6);
  border-radius: 0.5rem;
  padding: 2rem;
}

.empty-bank-accounts i {
  opacity: 0.5;
}

/* Divider */
hr {
  border-top: 1px solid var(--cui-border-color, #dee2e6);
}

/* Form Footer */
.form-footer {
  background: var(--cui-body-bg, #f8f9fa);
  border-top: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.25rem;
}

/* Responsive Adjustments */
@media (max-width: 767.98px) {
  .subcompany-form-wrapper {
    padding: 1rem;
  }

  .page-header h4 {
    font-size: 1.25rem;
  }

  .form-card .card-body {
    padding: 1.5rem 1rem;
  }

  .col-form-label {
    margin-bottom: 0.5rem;
  }
}

/* Print Styles */
@media print {
  .toast-container,
  .form-footer {
    display: none !important;
  }

  .form-card {
    box-shadow: none;
    border: 1px solid #dee2e6;
  }
}
</style>
